import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Tooltip, Menu, MenuItem, Box } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopIcon from '@mui/icons-material/Stop';
import { Select } from 'ol/interaction';
import { Fill, Stroke, Style } from 'ol/style';
import { unByKey } from 'ol/Observable';
import {
  addInfoFeature,
  removeInfoFeature,
} from '../redux/actions/infoFeature';
import {
  queryGeojsonToLayer,
  removeQueryLayer,
  queryGeojsonToLayerProduct,
  removeQueryLayerProduct,
  queryGeojsonToLayerEylulYesil,
  removeQueryLayerEylulYesil,
} from '../utils/geojson.tools';
import { startTrackPosition, stopTrackPosition } from '../utils/geolocation';
import { innerColor, themeColor, modernDesignSystem } from '../constants/themes';
import DrawPanel from './MapPanels/DrawPanel';
import MeasurePanel from './MapPanels/MeasurePanel';
import DataImpExpPanel from './MapPanels/DataImpExpPanel';
import { queryParcelFeatureURL } from '../api';
import fetchEylulYesilForParcel from '../utils/eylulYesil';

// Modern Tasarım Sistemi Referansı
const designSystem = modernDesignSystem;

const BottomNavigatorComponent = () => {
  const dispatch = useDispatch();
  const { map } = useSelector((state) => state.globalMaps[0]);

  const [statusLocation, setStatusLocation] = useState(false);
  const [statusQuery, setStatusQuery] = useState(false);
  const [clickEventKey, setClickEventKey] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const handleClick = (event) => {
    if (event.currentTarget.id === 'konum') {
      setAnchorEl(event.currentTarget);
    } else if (event.currentTarget.id === 'oznitelik') {
      setAnchorEl2(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEl2(null);
  };

  const [visibleDraw, setVisibleDraw] = useState('none');
  const [visibleMeasure, setVisibleMeasure] = useState('none');
  const [visibleParcel, setVisibleParcel] = useState('none');

  const selectInteractionRef = useRef(null);
  const queryEventHandler = (event) => {
    const clickedCoordinate = event.coordinate;
    axios
      .post(queryParcelFeatureURL, {
        coordinate: clickedCoordinate,
      })
      .then(async (response) => {
        try {
          const fromService = await fetchEylulYesilForParcel(response.data.parsel);
          if (fromService.list.length > 0) {
            response.data.parsel.properties.eylulYesil = fromService.list;
            response.data.eylulYesil = fromService.collection;
          }
        } catch (wfsError) {
          console.error(wfsError);
        }
        dispatch(addInfoFeature(response.data.parsel));
        // Ekleme sırası da çizimi etkilediği için alttan üste:
        // Eylül Yeşil, ürün, parsel sınırı.
        queryGeojsonToLayerEylulYesil(response.data.eylulYesil, map);
        queryGeojsonToLayerProduct(response.data.urunler, map);
        const layer = queryGeojsonToLayer(response.data.parsel, map);
        map.getLayers().remove(layer);
        map.getLayers().push(layer);
        const feature = layer.getSource().getFeatures()[0];
        if (selectInteractionRef.current) {
          map.removeInteraction(selectInteractionRef.current);
          selectInteractionRef.current = null;
        }
        if (feature) {
          const selectInteraction = new Select({
            // Varsayılan Select stili yarı saydam beyaz dolgu içeriyor ve bu,
            // ürün etiketlerinin hemen örtülmüş gibi görünmesine (hızlıca
            // kaybolmasına) neden oluyordu. Sadece kalın bir çerçeve ile
            // vurgulayıp dolguyu saydam bırakıyoruz.
            style: new Style({
              stroke: new Stroke({
                color: '#FFD54F',
                width: 4,
              }),
              fill: new Fill({
                color: 'rgba(0, 0, 0, 0)',
              }),
            }),
          });
          selectInteraction.getFeatures().push(feature);
          map.addInteraction(selectInteraction);
          selectInteractionRef.current = selectInteraction;
        }
      })
      .catch((error) => {
        removeQueryLayer(map);
        removeQueryLayerProduct(map);
        removeQueryLayerEylulYesil(map);
        dispatch(removeInfoFeature());
        const message =
          error.response?.data?.error || 'Sorgulamada bir hata oluştu';
        toast.error(message, {
          position: 'top-center',
          autoClose: 5000,
          theme: 'dark',
        });
      });
  };

  const startQueryFeatures = () => {
    const key = map.on('click', queryEventHandler);
    setClickEventKey(key);
    handleClose();
    setStatusQuery(true);
    toast.success('Öznitelik sorgulama aktif hale getirildi!', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });
  };

  const stopQueryFeatures = () => {
    if (clickEventKey) {
      unByKey(clickEventKey);
    }
    removeQueryLayerProduct(map);
    removeQueryLayerEylulYesil(map);
    removeQueryLayer(map);
    if (selectInteractionRef.current) {
      map.removeInteraction(selectInteractionRef.current);
      selectInteractionRef.current = null;
    }
    handleClose();
    setStatusQuery(false);
    toast.info('Öznitelik sorgulama pasif hale getirildi!', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });
  };

  const startTracking = () => {
    alert('Konum doğruluğu kullandığınız servis sağlayıcınıza bağlıdır!');
    startTrackPosition(map);
    handleClose();
    setStatusLocation(true);
    toast.success('Konum takibi aktif hale getirildi!', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });
  };

  const stopTracking = () => {
    stopTrackPosition();
    handleClose();
    setStatusLocation(false);
    toast.info('Konum takibi pasif hale getirildi!', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
    });
  };

  return (
    <>
      <div style={{ position: 'absolute', top: '35%', left: '45%' }}>
        <DrawPanel
          basemap={map}
          visible={visibleDraw}
          visibleFunc={setVisibleDraw}
        />
        <MeasurePanel
          basemap={map}
          visible={visibleMeasure}
          visibleFunc={setVisibleMeasure}
        />
        <DataImpExpPanel
          basemap={map}
          visible={visibleParcel}
          visibleFunc={setVisibleParcel}
        />
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          // Tablet ve mobil uyumluluk
          minWidth: 'fit-content',
          width: 'auto',
          maxWidth: '95vw',
        }}
      >
        <Box 
          display="flex" 
          justifyContent="center" 
          marginBottom={2}
          sx={{
            flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Küçük ekranlarda wrap
            gap: 1, // Butonlar arası boşluk
            alignItems: 'center',
            // Tablet için özel ayarlar
            '@media (max-width: 1024px)': {
              transform: 'scale(0.9)', // Tablet'te biraz küçült
            },
            '@media (max-width: 768px)': {
              transform: 'scale(0.8)', // Mobil'de daha da küçült
              flexWrap: 'wrap',
              maxWidth: '90vw',
            }
          }}
        >
          <Tooltip title="Konumu Bul" arrow>
            <Button
              id="konum"
              onClick={handleClick}
              variant="contained"
              sx={{
                backgroundColor: statusLocation 
                  ? designSystem.colors.semantic.success 
                  : designSystem.colors.primary.main,
                color: designSystem.colors.neutral.white,
                borderRadius: '50px',
                minWidth: { xs: '45px', sm: '52px' },
                height: { xs: '45px', sm: '52px' },
                marginRight: '5px',
                padding: { xs: '8px', sm: '12px' },
                boxShadow: designSystem.shadows.elevated,
                border: `2px solid ${designSystem.colors.primary.surface}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& i': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                // Hover efektleri
                '&:hover': {
                  backgroundColor: statusLocation 
                    ? designSystem.colors.semantic.success 
                    : designSystem.colors.primary.dark,
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: designSystem.shadows.heavy,
                },
                // Aktif durum
                ...(statusLocation && {
                  boxShadow: `0 4px 16px ${designSystem.colors.semantic.success}40`,
                }),
                // Touch-friendly ayarlar
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <i className="fg-location-arrow fg-2x"></i>
            </Button>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: anchorEl ? anchorEl.getBoundingClientRect().top : 0,
              left: anchorEl ? anchorEl.getBoundingClientRect().left - 25 : 0,
            }}
            style={{ marginTop: '-70px' }}
            PaperProps={{
              elevation: 0,
              style: {
                borderRadius: '16px',
                backgroundColor: designSystem.colors.neutral.white, // Beyaz arka plan
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                border: `1px solid ${designSystem.colors.primary.main}`,
                boxShadow: designSystem.shadows.elevated,
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <MenuItem
              disabled={statusLocation}
              sx={{
                borderRadius: '12px',
                margin: '4px',
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                '&:hover': {
                  backgroundColor: designSystem.colors.primary.surface, // Açık yeşil hover
                  color: designSystem.colors.primary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: designSystem.colors.neutral.gray100,
                  color: designSystem.colors.neutral.gray400,
                }
              }}
              onClick={startTracking}
            >
              <PlayCircleOutlineIcon sx={{ 
                marginRight: '8px',
                color: designSystem.colors.primary.main 
              }} />
              Başlat
            </MenuItem>
            <MenuItem
              disabled={!statusLocation}
              sx={{
                borderRadius: '12px',
                margin: '4px',
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                '&:hover': {
                  backgroundColor: designSystem.colors.primary.surface, // Açık yeşil hover
                  color: designSystem.colors.primary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: designSystem.colors.neutral.gray100,
                  color: designSystem.colors.neutral.gray400,
                }
              }}
              onClick={stopTracking}
            >
              <StopIcon sx={{ 
                marginRight: '8px',
                color: designSystem.colors.semantic.error 
              }} />
              Durdur
            </MenuItem>
          </Menu>

          <Tooltip title="Öznitelik Sorgula" arrow>
            <Button
              id="oznitelik"
              onClick={handleClick}
              variant="contained"
              sx={{
                backgroundColor: statusQuery 
                  ? designSystem.colors.semantic.success 
                  : designSystem.colors.secondary.main,
                color: designSystem.colors.neutral.white,
                borderRadius: '50px',
                minWidth: { xs: '45px', sm: '52px' },
                height: { xs: '45px', sm: '52px' },
                marginRight: '5px',
                padding: { xs: '8px', sm: '12px' },
                boxShadow: designSystem.shadows.elevated,
                border: `2px solid ${designSystem.colors.secondary.surface}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& i': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                // Hover efektleri
                '&:hover': {
                  backgroundColor: statusQuery 
                    ? designSystem.colors.semantic.success 
                    : designSystem.colors.secondary.dark,
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: designSystem.shadows.heavy,
                },
                // Aktif durum
                ...(statusQuery && {
                  boxShadow: `0 4px 16px ${designSystem.colors.semantic.success}40`,
                }),
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <i className="fg-poi-info fg-2x"></i>
            </Button>
          </Tooltip>
          <Menu
            anchorEl={anchorEl2}
            open={Boolean(anchorEl2)}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: anchorEl2 ? anchorEl2.getBoundingClientRect().top : 0,
              left: anchorEl2 ? anchorEl2.getBoundingClientRect().left - 25 : 0,
            }}
            style={{ marginTop: '-70px' }}
            PaperProps={{
              elevation: 0,
              style: {
                borderRadius: '16px',
                backgroundColor: designSystem.colors.neutral.white, // Beyaz arka plan
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                border: `1px solid ${designSystem.colors.secondary.main}`,
                boxShadow: designSystem.shadows.elevated,
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <MenuItem
              disabled={statusQuery}
              sx={{
                borderRadius: '12px',
                margin: '4px',
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                '&:hover': {
                  backgroundColor: designSystem.colors.secondary.surface, // Açık yeşil hover
                  color: designSystem.colors.secondary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: designSystem.colors.neutral.gray100,
                  color: designSystem.colors.neutral.gray400,
                }
              }}
              onClick={startQueryFeatures}
            >
              <PlayCircleOutlineIcon sx={{ 
                marginRight: '8px',
                color: designSystem.colors.secondary.main 
              }} />
              Başlat
            </MenuItem>
            <MenuItem
              disabled={!statusQuery}
              sx={{
                borderRadius: '12px',
                margin: '4px',
                color: designSystem.colors.neutral.gray800, // Koyu yazı
                '&:hover': {
                  backgroundColor: designSystem.colors.secondary.surface, // Açık yeşil hover
                  color: designSystem.colors.secondary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: designSystem.colors.neutral.gray100,
                  color: designSystem.colors.neutral.gray400,
                }
              }}
              onClick={stopQueryFeatures}
            >
              <StopIcon sx={{ 
                marginRight: '8px',
                color: designSystem.colors.semantic.error 
              }} />
              Durdur
            </MenuItem>
          </Menu>
          <Tooltip title="Çizim Araçları" arrow>
            <Button
              onClick={() => setVisibleDraw('block')}
              variant="contained"
              sx={{
                backgroundColor: designSystem.colors.tertiary.main,
                color: designSystem.colors.neutral.white,
                borderRadius: '50px',
                minWidth: { xs: '45px', sm: '52px' },
                height: { xs: '45px', sm: '52px' },
                marginRight: '5px',
                padding: { xs: '8px', sm: '12px' },
                boxShadow: designSystem.shadows.elevated,
                border: `2px solid ${designSystem.colors.tertiary.surface}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& i': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                '&:hover': {
                  backgroundColor: designSystem.colors.tertiary.dark,
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: designSystem.shadows.heavy,
                },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <i className="fg-polygon-pt fg-2x"></i>
            </Button>
          </Tooltip>
          <Tooltip title="Ölçüm Araçları" arrow>
            <Button
              onClick={() => setVisibleMeasure('block')}
              variant="contained"
              sx={{
                backgroundColor: designSystem.colors.accent.blue,
                color: designSystem.colors.neutral.white,
                borderRadius: '50px',
                minWidth: { xs: '45px', sm: '52px' },
                height: { xs: '45px', sm: '52px' },
                marginRight: '5px',
                padding: { xs: '8px', sm: '12px' },
                boxShadow: designSystem.shadows.elevated,
                border: `2px solid ${designSystem.colors.neutral.gray200}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& i': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                '&:hover': {
                  backgroundColor: '#1565C0', // Koyu mavi
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: designSystem.shadows.heavy,
                },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <i className="fg-measure fg-2x"></i>
            </Button>
          </Tooltip>
          <Tooltip title="Veri İndir/Yükle" arrow>
            <Button
              onClick={() => setVisibleParcel('block')}
              variant="contained"
              sx={{
                backgroundColor: designSystem.colors.accent.teal,
                color: designSystem.colors.neutral.white,
                borderRadius: '50px',
                minWidth: { xs: '45px', sm: '52px' },
                height: { xs: '45px', sm: '52px' },
                marginRight: '5px',
                padding: { xs: '8px', sm: '12px' },
                boxShadow: designSystem.shadows.elevated,
                border: `2px solid ${designSystem.colors.neutral.gray200}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& i': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                },
                '&:hover': {
                  backgroundColor: '#004D40', // Koyu teal
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: designSystem.shadows.heavy,
                },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <i className="fg-layer-download fg-2x"></i>
            </Button>
          </Tooltip>
        </Box>
      </div>
    </>
  );
};

export default BottomNavigatorComponent;
